import { Attribute } from '../../decorators/attribute.decorator';
import { Component } from '../../decorators/component.decorator';
import { TabIndex } from '../../global/keyboard';
import type { NullOr } from '../../global/utils.type';
import { ComponentState } from '../ui-element.const';
import { UIElement } from '../ui-element';
import type { AttributeValue, ObservedAttributes } from '../ui-element.type';
import styles from '../scrollable-frame/ui-scrollable-frame.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIScrollableFrame
 * @element ui-scrollable-frame
 * @classdesc Represents a class for <code>ui-scrollable-frame</code> element.
 * Frame with given max height and scroll if the content is too huge.
 * @property {string} [maxHeight="140px"] {@attr max-height} Fixed height for frame.
 * @slot
 * @example
 * <ui-scrollable-frame max-height="10vh">
 *   <p>Praesent commodo cursus magna, vel scelerisque nisl consectetur et.
 *   Aenean eu leo quam. Pellentesque ornare sem lacinia quam venenatis vestibulum.
 *   Nullam quis risus eget urna mollis ornare vel eu leo.</p>
 *   <p>Praesent commodo cursus magna, vel scelerisque nisl consectetur et.
 *   Aenean eu leo quam. Pellentesque ornare sem lacinia quam venenatis vestibulum.
 *   Nullam quis risus eget urna mollis ornare vel eu leo.</p>
 * </ui-scrollable-frame>
 */
@Component({ tag: 'ui-scrollable-frame', styles })
export class UIScrollableFrame extends UIElement {
    /**
     * @type {string[]}
     */
    static get observedAttributes(): ObservedAttributes {
        return ['max-height'];
    }

    @Attribute(String) maxHeight: string = '140px';

    /**
     * Updates height styles for the frame
     */
    updateAppearance(): void {
        this.style.maxHeight = this.maxHeight || '140px';
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: string,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (this.state !== ComponentState.Hydrated) {
            return;
        }

        switch (name) {
            case 'max-height':
                this.updateAppearance();
                break;
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        // Set tabindex to allow scroll with arrows without scrolling the window.
        this.setAttribute('tabindex', TabIndex.Active);
        this.updateAppearance();
    }
}
